import { ApolloClient, ApolloLink, InMemoryCache, HttpLink, Observable } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';

import type { AuthService, TokenPayload } from 'app/services/auth';
import type { HyroxMirror } from 'app/utils/hyrox-mirror';
import { createHyroxMirrorLink } from 'app/utils/hyrox-mirror/link';

import { getPersistedState } from 'state/auth';

type GetClientArgs = {
  uri: string;
  authService: AuthService;
  oneFiitAuthEnabled?: boolean;
  mirror?: HyroxMirror | null;
  onTokenRefresh: (payload: TokenPayload) => void;
  onTokenRefreshError: (error: Error) => void;
};

const TRANSPORT_401_REFRESH_CYCLE_LIMIT = 3;

const isTransport401Error = (error: unknown): boolean =>
  error !== null && typeof error === 'object' && 'statusCode' in error && error.statusCode === 401;

type AuthHeaders = {
  authorization: string | null;
};
type RefreshRequest = Promise<AuthHeaders>;

type GetAuthHeadersArgs = {
  refreshToken: string | null;
  authorization: string | null;
};

export default ({
  uri,
  authService,
  oneFiitAuthEnabled,
  mirror,
  onTokenRefresh,
  onTokenRefreshError,
}: GetClientArgs) => {
  const httpLink = new HttpLink({ uri });
  const cache = new InMemoryCache({
    typePolicies: {
      User: {
        fields: {
          permissions: {
            merge: true,
          },
        },
      },
    },
  });

  let refreshRequest: RefreshRequest | null = null;
  let refreshRequestToken: string | null = null;
  let transport401RefreshCycles = 0;

  const getAuthHeaders = async ({ refreshToken, authorization }: GetAuthHeadersArgs): RefreshRequest => {
    // If we have an auth header stored, use it
    if (authorization) {
      return { authorization };
    }

    // If there's no auth header, we need to get one - but if we don't have a refresh token then we're fucked
    if (!refreshToken) {
      onTokenRefreshError(new Error('Cannot obtain new authorization without a refresh token'));

      return { authorization: null };
    }

    // Single-flight per token: one-fiit refresh tokens are one-time-use, so concurrent 401s must share one
    // refresh, but a flight started with a stale token must not capture callers holding a rotated one
    if (refreshRequest === null || refreshRequestToken !== refreshToken) {
      const request: RefreshRequest = authService
        .refreshToken({ refreshToken })
        .then((response) => {
          // logger.debug('refreshing tokens');

          // Store the new tokens
          onTokenRefresh(response);

          // Retrieve the new tokens in the right format (we could also reformat here but :shrug:)
          // This also requires that onTokenRefresh complete it's side effect before proceeding
          const { authorization: newAuthorization } = getPersistedState();

          return { authorization: newAuthorization };
        })
        .catch((refreshError) => {
          // A newer flight superseded this one; defer to it instead of signing out a recoverable session
          if (refreshRequest !== null && refreshRequest !== request) {
            return refreshRequest;
          }

          // Stale failure after a rotation elsewhere (e.g. another tab) must not sign out
          const { authorization: rotatedAuthorization, refreshToken: currentRefreshToken } = getPersistedState();
          if (currentRefreshToken !== null && currentRefreshToken !== refreshToken) {
            return { authorization: rotatedAuthorization };
          }

          onTokenRefreshError(refreshError);

          return { authorization: null };
        })
        .finally(() => {
          if (refreshRequest === request) {
            refreshRequest = null;
            refreshRequestToken = null;
          }
        });

      refreshRequest = request;
      refreshRequestToken = refreshToken;
    }

    return refreshRequest;
  };

  const getTimezoneHeader = (): Record<string, string> => {
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return timeZone ? { 'x-timezone': timeZone } : {};
    } catch {
      return {};
    }
  };

  const getContext = async ({ headers = {}, ...context }) => {
    const { authorization, refreshToken } = getPersistedState();
    const authHeaders = await getAuthHeaders({ authorization, refreshToken });
    return {
      ...context,
      headers: {
        ...headers,
        ...(authHeaders?.authorization ? authHeaders : {}),
        ...getTimezoneHeader(),
      },
    };
  };

  const authLink = setContext(async (_, { headers }) => {
    const { authorization, refreshToken } = getPersistedState();
    const authHeaders = await getAuthHeaders({ authorization, refreshToken });
    return {
      headers: {
        ...headers,
        ...(authHeaders?.authorization ? authHeaders : {}),
        ...getTimezoneHeader(),
      },
    };
  });

  const refreshTokenLink = onError(({ operation, graphQLErrors = [], networkError, forward }) => {
    const { refreshToken } = getPersistedState();
    // The transport-401 trigger only exists for one-fiit (bare 401, no errors envelope) and stays
    // behind the gate; the circuit-breaker stops cross-operation refresh churn (and the resulting
    // burn of one-time-use refresh tokens) when an infra-level 401 persists across retries
    const isTransport401 = oneFiitAuthEnabled === true && isTransport401Error(networkError);
    const isRefreshableTransport401 = isTransport401 && transport401RefreshCycles < TRANSPORT_401_REFRESH_CYCLE_LIMIT;
    const authError =
      graphQLErrors.some(({ extensions }) => extensions?.code === 'UNAUTHENTICATED') || isRefreshableTransport401;

    if (authError && refreshToken) {
      return new Observable((observer) => {
        getAuthHeaders({ refreshToken, authorization: null })
          .then(async () => {
            const oldContext = operation.getContext();
            const newContext = await getContext(oldContext);
            operation.setContext(newContext);

            // Retry the request
            forward(operation).subscribe({
              next: (value) => observer.next(value),
              error: (retryError: unknown) => {
                if (isTransport401 && isTransport401Error(retryError)) {
                  transport401RefreshCycles += 1;
                }
                observer.error(retryError);
              },
              complete: () => observer.complete(),
            });
          })
          .catch((refreshError: Error) => {
            // Token refresh failed - this will trigger signout via onTokenRefreshError
            onTokenRefreshError(refreshError);
            observer.error(refreshError);
          });
      });
    }

    // If we have an auth error but no refresh token, sign out immediately
    if (authError && !refreshToken) {
      onTokenRefreshError(new Error('Authentication failed and no refresh token available'));
    }
  });

  const transport401ResetLink = new ApolloLink((operation, forward) =>
    forward(operation).map((result) => {
      transport401RefreshCycles = 0;
      return result;
    }),
  );

  // Sits right before httpLink so it sees the final primary result (a 401 → refresh → retry re-forwards through it)
  const mirrorLink = mirror ? createHyroxMirrorLink({ ...mirror, getTimezoneHeader }) : null;

  const client = new ApolloClient({
    link: ApolloLink.from(
      [authLink, refreshTokenLink, transport401ResetLink, mirrorLink, httpLink].filter(
        (link): link is ApolloLink => link !== null,
      ),
    ),
    cache,
  });

  return {
    client,
  };
};
